import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Panel } from "@/components/common";
import { TextField, SelectField, FieldWrap } from "@/components/fields";
import { StatusSelect } from "@/components/StatusSelect";
import { AU_STATES } from "@/lib/constants";
import { jobTypeNames } from "@/lib/jobTypes";
import { api } from "@/lib/api";

const leadOptions = (settings) => {
  const companies = settings.companies.map((c) => c.name);
  const own = (settings.lead_sources || []).filter((l) => !companies.some((c) => c.toLowerCase() === l.toLowerCase()));
  return [...own, ...companies];
};
import { fmtDate } from "@/lib/format";

const DuplicateWarning = ({ number, excludeId }) => {
  const [dups, setDups] = useState([]);
  useEffect(() => {
    const n = number.trim();
    if (!n) return setDups([]);
    const t = setTimeout(() => {
      api.get("/bookings/check-number", { params: { number: n, exclude: excludeId || "" } }).then((r) => setDups(r.data.duplicates)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [number, excludeId]);
  if (!dups.length) return null;
  return (
    <div className="mt-2 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="duplicate-job-warning">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div>
        <div className="font-semibold">Job number {number.trim()} is already used on {dups.length} other booking{dups.length > 1 ? "s" : ""}.</div>
        <ul className="mt-1 space-y-0.5">
          {dups.map((d) => (
            <li key={d.id}><Link to={`/bookings/${d.id}`} className="underline">{d.customer_name}</Link> · {fmtDate(d.moving_date, "d MMM yyyy")} · {d.status}</li>
          ))}
        </ul>
        <div className="mt-1 text-amber-700">You can still save – some customers book multiple jobs under one number.</div>
      </div>
    </div>
  );
};

export const JobSection = ({ form, set, errors, settings, excludeId }) => (
  <Panel title="Job information" id="section-job">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <div className="sm:col-span-2 xl:col-span-1">
        <TextField name="job_number" label="Move Pro Job Number" required placeholder="e.g. 10432" hint="Copy this from Move Pro" form={form} set={set} errors={errors} className="[&_input]:font-mono [&_input]:font-semibold" />
        <DuplicateWarning number={form.job_number} excludeId={excludeId} />
      </div>
      <TextField name="moving_date" label="Moving date" type="date" required form={form} set={set} errors={errors} />
      <FieldWrap label="Booking status" name="status" error={errors.status}><StatusSelect value={form.status} onChange={(v) => set("status", v)} testId="field-status" /></FieldWrap>
      <SelectField name="job_type" label="Job type / route" options={jobTypeNames(settings.job_types)} form={form} set={set} errors={errors} />
      <SelectField name="state" label="State" options={AU_STATES} form={form} set={set} errors={errors} />
      <TextField name="start_time" label="Start time" type="time" form={form} set={set} errors={errors} />
      <TextField name="confirmation_time" label="Time shown in customer confirmation" placeholder="e.g. 7:00am – 8:00am arrival" form={form} set={set} errors={errors} />
      <SelectField name="lead_source" label="Lead source / where the job came from" options={leadOptions(settings)} hint={form.bill_to_company ? `Billed to ${form.bill_to_company}` : "Picking a partner company also sets 'Bill to company'"} form={form} set={(k, v) => { set("lead_source", v); const co = settings.companies.find((c) => c.name === v); if (co) set("bill_to_company", co.name); else if (settings.companies.some((c) => c.name === form.lead_source)) set("bill_to_company", ""); }} errors={errors} />
      <SelectField name="sales_person" label="Booked by / sales person" options={settings.sales_people} form={form} set={set} errors={errors} />
    </div>
  </Panel>
);

export const CustomerSection = ({ form, set, errors }) => (
  <Panel title="Customer information" id="section-customer">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <TextField name="customer_name" label="Customer full name" required form={form} set={set} errors={errors} />
      <TextField name="mobile" label="Mobile number" required type="tel" placeholder="0412 345 678" form={form} set={set} errors={errors} />
      <TextField name="email" label="Email address" type="email" placeholder="name@email.com" form={form} set={set} errors={errors} />
      <TextField name="emergency_contact_name" label="Emergency contact name" form={form} set={set} errors={errors} />
      <TextField name="emergency_contact_number" label="Emergency contact number" type="tel" form={form} set={set} errors={errors} />
    </div>
  </Panel>
);
